import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

import { baseUrl } from "../../api";
import { renderBeefCutCard } from "./beefCutCardRenderer";
import {
  createPdfBlobUrl,
  embedImageFromUrl,
  generateRoundedQRDataUrl,
  hexToRgb,
} from "./pdfUtils";
import {
  PDF_TEMPLATE_ID,
  CLEAR_AREA,
  CARD_CONFIG,
  HEADER_BAND_PADDING_X,
  POSTER_HEADER_COLOR_STRIP_X,
  POSTER_MAIN_TITLE,
  POSTER_TEMPLATE_FOOTER_MASK_HEIGHT,
} from "./pdfConstants";

/** Same host as `<img src={.../assets/}>` so PDF generation loads retailer files. */
const assetsBase = () => process.env.REACT_APP_API_URL || baseUrl;

const normalizeAssetId = (value) => {
  if (value == null) return null;
  if (typeof value === "object" && value.id != null) {
    const id = String(value.id).trim();
    return id || null;
  }
  const s = String(value).trim();
  return s || null;
};

/**
 * Generate beef merchandising PDF with selected beef cuts
 * @param {object} [posterOptions]
 * @param {string|null|undefined} [posterOptions.headerCenterLogoAssetId] When set to a non-empty id, used as the center header logo. Empty string = no logo. When the key is omitted, or null/undefined, uses `retailer.logo`.
 * @param {string|undefined} [posterOptions.posterTitle] When non-empty, drawn as the main poster title on the left of the header band.
 */
export const generateBeefMerchandisingPDF = async (
  selectedBeefCuts,
  beefCuts,
  retailer,
  languageCode = "en-US",
  posterOptions = {},
) => {
  const hasHeaderLogoKey =
    posterOptions &&
    Object.prototype.hasOwnProperty.call(posterOptions, "headerCenterLogoAssetId");
  const rawHeaderLogo = hasHeaderLogoKey
    ? posterOptions.headerCenterLogoAssetId
    : undefined;
  const centerHeaderLogoId =
    rawHeaderLogo === ""
      ? null
      : normalizeAssetId(rawHeaderLogo) ?? normalizeAssetId(retailer?.logo);

  const posterTitleRaw =
    posterOptions && Object.prototype.hasOwnProperty.call(posterOptions, "posterTitle")
      ? String(posterOptions.posterTitle ?? "").trim()
      : "";
  // Load the PDF template
  const pdfUrl = `${baseUrl}/assets/${PDF_TEMPLATE_ID}`;
  const response = await fetch(pdfUrl);
  const existingPdfBytes = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  // Get the first page
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  // Clear the beef cuts content area
  firstPage.drawRectangle({
    x: CLEAR_AREA.X,
    y: CLEAR_AREA.Y,
    color: rgb(1, 1, 1),
    height: CLEAR_AREA.HEIGHT,
    width: CLEAR_AREA.WIDTH,
  });

  const FOOTER_X = POSTER_HEADER_COLOR_STRIP_X;
  const FOOTER_Y = 1062;
  const FOOTER_WIDTH = 276;
  const FOOTER_HEIGHT = 63;
  /** Horizontal extent of the beef cut grid (same as card columns). */
  const beefGridBandX = CARD_CONFIG.START_X;
  const beefGridBandWidth =
    CARD_CONFIG.COLUMNS * CARD_CONFIG.CARD_WIDTH +
    (CARD_CONFIG.COLUMNS - 1) * CARD_CONFIG.SPACING_X;
  /** Drawn QR side length; inset inside HEADER_QR_CELL for breathing room in the header band. */
  const HEADER_QR_DRAW_SIZE = 42;
  const HEADER_QR_PADDING = 4;
  const HEADER_QR_CELL = HEADER_QR_DRAW_SIZE + 2 * HEADER_QR_PADDING;
  /** Right edge of the QR / partner header column (used for layout whether or not a QR is present). */
  const headerColumnRight = FOOTER_X + FOOTER_WIDTH - HEADER_BAND_PADDING_X;
  const qrCellLeft = headerColumnRight - HEADER_QR_CELL;
  const qrCellBottom = FOOTER_Y + (FOOTER_HEIGHT - HEADER_QR_CELL) / 2;
  const qrDrawX = qrCellLeft + HEADER_QR_PADDING;
  const qrDrawY = qrCellBottom + HEADER_QR_PADDING;

  if (posterTitleRaw) {
    firstPage.drawRectangle({
      x: beefGridBandX,
      y: FOOTER_Y,
      width: beefGridBandWidth,
      height: FOOTER_HEIGHT,
      color: rgb(0, 0, 0),
    });
  }

  // Customize top bar with retailer brand color and diagonal cut
  if (retailer?.brand_color) {
    const brandColor = hexToRgb(retailer.brand_color);


    // Draw main rectangle
    firstPage.drawRectangle({
      x: FOOTER_X,
      y: FOOTER_Y,
      width: FOOTER_WIDTH,
      height: FOOTER_HEIGHT,
      color: brandColor,
    });

    const triangleWidth = 30;
    const steps = 120; // Number of rectangles to create smooth diagonal
    // Create diagonal triangle on right side with edge at top
    const triangleStartX = FOOTER_X - triangleWidth;

    for (let i = 0; i < steps; i++) {
      const progress = i / steps;
      const currentHeight = FOOTER_HEIGHT * progress; // Increases from 0 to full
      const stepWidth = triangleWidth / steps;

      firstPage.drawRectangle({
        x: triangleStartX + (i * stepWidth),
        y: FOOTER_Y,
        width: stepWidth + 1, // +1 to avoid gaps
        height: currentHeight,
        color: brandColor,
      });
    }
  }

  // Embed fonts
  const [helveticaFont, helveticaBold] = await Promise.all([
    pdfDoc.embedFont(StandardFonts.Helvetica),
    pdfDoc.embedFont(StandardFonts.HelveticaBold),
  ]);
  const fonts = {
    regular: helveticaFont,
    bold: helveticaBold,
  };

  // Get selected beef cuts; sort A-Z; cap at grid size
  const selectedBeefCutsData = selectedBeefCuts
    .map((cutId) => beefCuts?.find((cut) => cut.id === cutId))
    .filter(Boolean)
    .sort((a, b) => {
      const nameA = a.translations?.[0]?.name ?? "";
      const nameB = b.translations?.[0]?.name ?? "";
      return nameA.localeCompare(nameB);
    })
    .slice(0, CARD_CONFIG.MAX_CARDS);

  // Prepare all async operations to run in parallel
  const parallelTasks = [];

  // Task: Add center header logo (poster override or retailer logo)
  if (centerHeaderLogoId) {
    const logoTask = (async () => {
      try {
        const logoUrl = `${assetsBase()}/assets/${centerHeaderLogoId}`;
        const logoImage = await embedImageFromUrl(pdfDoc, logoUrl);

        if (logoImage) {
          // Calculate centered position
          const logoX = FOOTER_X + (FOOTER_WIDTH - 75) / 2 + 10;
          const logoY = FOOTER_Y + (FOOTER_HEIGHT - 40) / 2;

          return { type: 'logo', image: logoImage, x: logoX, y: logoY, width: 75, height: 40 };
        }
      } catch (error) {
        console.error("Error embedding retailer logo:", error);
      }
      return null;
    })();
    parallelTasks.push(logoTask);
  }

  // Task: Optional partner logo in the top-right header (beside QR or flush right if no QR)
  const partnerNameTrimmed = retailer?.poster_partner_name?.trim?.() ?? "";
  if (retailer?.poster_partner_logo) {
    const partnerLogoTask = (async () => {
      try {
        const logoUrl = `${assetsBase()}/assets/${normalizeAssetId(retailer.poster_partner_logo)}`;
        const logoImage = await embedImageFromUrl(pdfDoc, logoUrl);
        if (!logoImage) return null;

        const raw = logoImage.scale(1);
        const maxW = 72;
        const maxH = 36;
        const scale = Math.min(maxW / raw.width, maxH / raw.height, 1);
        const width = raw.width * scale;
        const height = raw.height * scale;

        const gap = 6;
        let x;
        let y;
        if (retailer?.store_url) {
          x = qrCellLeft - gap - width;
          y = qrDrawY + (HEADER_QR_DRAW_SIZE - height) / 2;
        } else {
          x = headerColumnRight - width;
          y = FOOTER_Y + (FOOTER_HEIGHT - height) / 2;
        }

        return {
          type: "posterPartnerLogo",
          image: logoImage,
          x,
          y,
          width,
          height,
        };
      } catch (error) {
        console.error("Error embedding poster partner logo:", error);
      }
      return null;
    })();
    parallelTasks.push(partnerLogoTask);
  }

  // Task: Add QR code to the right of retailer logo
  if (retailer?.store_url) {
    const qrTask = (async () => {
      try {
        const qrDataUrl = await generateRoundedQRDataUrl(retailer.store_url);
        if (qrDataUrl) {
          const qrImage = await pdfDoc.embedPng(qrDataUrl);

          return {
            type: "qr",
            image: qrImage,
            x: qrDrawX,
            y: qrDrawY,
            size: HEADER_QR_DRAW_SIZE,
          };
        }
      } catch (error) {
        console.error("Error embedding retailer QR code:", error);
      }
      return null;
    })();
    parallelTasks.push(qrTask);
  }

  // Tasks: Render beef cut cards in parallel
  const cardTasks = selectedBeefCutsData.map((beefCut, index) => {
    // Calculate grid position (4 columns, 8 rows)
    const row = Math.floor(index / CARD_CONFIG.COLUMNS);
    const col = index % CARD_CONFIG.COLUMNS;

    // Calculate X and Y position
    const cardX = CARD_CONFIG.START_X + (col * (CARD_CONFIG.CARD_WIDTH + CARD_CONFIG.SPACING_X));
    const cardY = CARD_CONFIG.START_Y - (row * (CARD_CONFIG.CARD_HEIGHT + CARD_CONFIG.SPACING_Y));

    return renderBeefCutCard(
      firstPage,
      pdfDoc,
      beefCut,
      fonts,
      cardX,
      cardY,
      CARD_CONFIG.CARD_WIDTH,
      languageCode,
      retailer,
    );
  });


  parallelTasks.push(...cardTasks);

  // Execute all tasks in parallel
  const results = await Promise.all(parallelTasks);

  const qrResult = results.find((r) => r?.type === "qr");
  const posterPartnerLogoResult = results.find((r) => r?.type === "posterPartnerLogo");

  const drawPartnerHeaderLabel = (qrBlock, labelBaselineY) => {
    if (!partnerNameTrimmed) return;

    const maxLabelWidth = qrBlock
      ? 120
      : Math.min(200, headerColumnRight - FOOTER_X - 16);
    let fontSize = 8;
    let text = partnerNameTrimmed;
    while (
      fontSize >= 6 &&
      helveticaBold.widthOfTextAtSize(text, fontSize) > maxLabelWidth
    ) {
      fontSize -= 0.5;
    }
    if (helveticaBold.widthOfTextAtSize(text, fontSize) > maxLabelWidth) {
      const ellipsis = "…";
      while (
        text.length > 0 &&
        helveticaBold.widthOfTextAtSize(text + ellipsis, fontSize) > maxLabelWidth
      ) {
        text = text.slice(0, -1);
      }
      text += ellipsis;
    }

    const textWidth = helveticaBold.widthOfTextAtSize(text, fontSize);
    let textX;
    let textY;
    if (typeof labelBaselineY === "number") {
      textX = headerColumnRight - textWidth;
      textY = labelBaselineY;
    } else if (qrBlock) {
      textX = qrBlock.x + (qrBlock.size - textWidth) / 2;
      textY = qrBlock.y + qrBlock.size + 2;
    } else {
      textX = headerColumnRight - textWidth;
      textY = FOOTER_Y + FOOTER_HEIGHT - 10;
    }

    firstPage.drawText(text, {
      x: textX,
      y: textY,
      size: fontSize,
      font: helveticaBold,
      color: rgb(1, 1, 1),
    });
  };

  const centerLogoResult = results.find((r) => r?.type === "logo");

  // Partner strip: partner logo → label → QR (center retailer logo drawn last so it is not covered)
  if (posterPartnerLogoResult) {
    firstPage.drawImage(posterPartnerLogoResult.image, {
      x: posterPartnerLogoResult.x,
      y: posterPartnerLogoResult.y,
      width: posterPartnerLogoResult.width,
      height: posterPartnerLogoResult.height,
    });
  }

  if (qrResult) {
    drawPartnerHeaderLabel(qrResult);
    firstPage.drawImage(qrResult.image, {
      x: qrResult.x,
      y: qrResult.y,
      width: qrResult.size,
      height: qrResult.size,
    });
  } else if (partnerNameTrimmed) {
    const nameY =
      posterPartnerLogoResult != null
        ? posterPartnerLogoResult.y - 4
        : undefined;
    drawPartnerHeaderLabel(null, nameY);
  }

  if (centerLogoResult) {
    firstPage.drawImage(centerLogoResult.image, {
      x: centerLogoResult.x,
      y: centerLogoResult.y,
      width: centerLogoResult.width,
      height: centerLogoResult.height,
    });
  }

  if (posterTitleRaw) {
    let fontSize = POSTER_MAIN_TITLE.FONT_SIZE_START;
    let text = posterTitleRaw.toUpperCase();
    const maxW = POSTER_MAIN_TITLE.MAX_WIDTH;
    while (
      fontSize > POSTER_MAIN_TITLE.FONT_SIZE_MIN &&
      helveticaBold.widthOfTextAtSize(text, fontSize) > maxW
    ) {
      fontSize -= 0.5;
    }
    if (helveticaBold.widthOfTextAtSize(text, fontSize) > maxW) {
      const ellipsis = "…";
      while (
        text.length > 0 &&
        helveticaBold.widthOfTextAtSize(text + ellipsis, fontSize) > maxW
      ) {
        text = text.slice(0, -1);
      }
      text += ellipsis;
    }
    const lineHeight = helveticaBold.heightAtSize(fontSize);
    const titleBaselineY = FOOTER_Y + (FOOTER_HEIGHT - lineHeight) / 2;
    firstPage.drawText(text, {
      x: POSTER_MAIN_TITLE.X,
      y: titleBaselineY,
      size: fontSize,
      font: helveticaBold,
      color: rgb(1, 1, 1),
    });
  }

  // Create and return PDF blob URL
  return await createPdfBlobUrl(pdfDoc);
};
